import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter, useHistory } from "react-router-dom";

import { PageTitle } from "../../components/Common/PageTitle";
import useFacilityReviews from "../../components/FacilityDetailsComponents/RecentReviewsSection/hooks/useFacilityReviews";
import ReviewItem from "../../components/FacilityDetailsComponents/ReviewItem";
import { canReplyFacilityReviews } from "../../components/FacilityDetailsComponents/ReviewItem/utils";
import { Checkbox } from "../../components/FormElement/Checkbox";
import { Pagination } from "../../components/Pagination";
import ReplyReviewModal from "../../components/ReviewsManagementPage/ReplyReviewModal";
import { useMe } from "../../hooks/queries";
import { ReviewData } from "../../models/ReviewData";

import "./styles.scss";

interface MatchParams {
  locationId: string;
  organizationId?: string;
}

type IReviewsManagementPageProps = RouteComponentProps<MatchParams>;

const ReviewsManagementPage: React.FunctionComponent<
  IReviewsManagementPageProps
> = ({ match }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`reviewsManagementPage.${key}`);
  const history = useHistory();
  const { data: me } = useMe();

  const locationId = match?.params?.locationId ?? "";
  const organizationId = match?.params?.organizationId;

  const [showUnrepliedOnly, setShowUnrepliedOnly] = useState(false);
  const [selectedReview, setSelectedReview] = useState<ReviewData | null>(null);
  const [filterFormData, setFilterFormData] = useState({
    pageIndex: 1,
    numberPerPage: 10,
  });

  const canReplyReviews = canReplyFacilityReviews(me);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { reviews, count, isLoading, refetch } = useFacilityReviews({
    locationId,
    sort: "review_date",
    order: "desc",
    limit: filterFormData.numberPerPage,
    offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage,
    isReplied: showUnrepliedOnly ? 0 : undefined,
    seek_total_count: true,
  });

  const handleBackClick = useCallback(() => {
    if (organizationId) {
      history.push(`/location/${locationId}/organization/${organizationId}`);
    } else {
      history.push(`/location/${locationId}`);
    }
  }, [history, locationId, organizationId]);

  const handleUnrepliedToggle = useCallback((checked: boolean) => {
    setShowUnrepliedOnly(checked);
    setFilterFormData((prev) => ({ ...prev, pageIndex: 1 }));
  }, []);

  const handleSendMessage = useCallback((review: ReviewData) => {
    setSelectedReview(review);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedReview(null);
  }, []);

  const handleReplySuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleFilterFormDataChange = useCallback((newFilterFormData: any) => {
    setFilterFormData(newFilterFormData);
  }, []);

  // Scroll to top when new review data is loaded (after page change)
  useEffect(() => {
    // Only scroll when we have reviews and not loading
    if (!isLoading && reviews && reviews.length > 0) {
      // Use a small delay to ensure the DOM is fully updated with new content
      // Try ref first
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
        return;
      }

      // Fallback to querySelector
      const scrollContainer = document.querySelector(
        ".reviews-scroll-container"
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [isLoading, reviews, filterFormData.pageIndex]);

  return (
    <div className="right-content reviews-management-page">
      <div className="back-top-bar">
        <button className="back-link" onClick={handleBackClick}>
          <i className="icons icon-back"></i>
          {t("backToFacilityDetails")}
        </button>
      </div>

      <div className="location-name">
        <PageTitle text={t("title")} />
        <span className="recent-reviews-warning">
          {t("recent_reviews_warning")}
        </span>
      </div>

      <div className="group-title">
        GBP
        <div className="line"></div>
      </div>

      <div className="filter-container">
        <Checkbox
          id="show-unreplied-only"
          checked={showUnrepliedOnly}
          label={t("showUnrepliedOnly")}
          onChange={handleUnrepliedToggle}
        />
      </div>

      <div className="reviews-list-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-text">{t("loading")}</div>
          </div>
        ) : (reviews || []).length === 0 ? (
          <div className="no-reviews-container">
            <div className="no-reviews-text">{t("noReviews")}</div>
          </div>
        ) : (
          <div className="reviews-scroll-container" ref={scrollContainerRef}>
            {(reviews || []).map((review: ReviewData) => (
              <ReviewItem
                key={review.review_id}
                review={review}
                canReply={true}
                onReply={canReplyReviews ? handleSendMessage : undefined}
                replyButtonText={t("sendMessage")}
              />
            ))}
          </div>
        )}
      </div>

      <div className="pagination-container">
        <Pagination
          filterFormData={filterFormData}
          totalRowsNumber={count}
          onChangeFilterFormData={handleFilterFormDataChange}
        />
      </div>

      <ReplyReviewModal
        review={selectedReview}
        locationId={locationId}
        canReply={canReplyReviews}
        onClose={handleCloseModal}
        onSuccess={handleReplySuccess}
      />
    </div>
  );
};

export default withRouter(ReviewsManagementPage);
